import { useSelector } from 'react-redux';
import { hapticsImpactLight } from '../../../utils/haptics';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { examsSelectors } from '../../../slices/exams/examsSlice';
import { TriangleRight } from '../../../assets/svgs/TriangleRight';
import { useEffect, useMemo, useRef, useState } from 'react';
import { RootState } from '../../../store/store';
import { UserExam } from '../../../types/User';
import { useUserExamUpdate } from '../../../utils/updateUserExam';
import {
  defaultFlashcards,
  defaultVignettes,
  isNbmeExam,
} from '../../../utils/examUtils';
import { TriangleDown } from '../../../assets/svgs/TriangleDown';
import Menu from '@mui/material/Menu';
import { Check, Plus } from 'react-feather';
import WorkloadSelect from '../../Global/Header/WorkloadSelect';
import ExamDateSelect from '../../Global/Header/ExamDateSelect';
import TopicSelect from '../../Global/Header/TopicSelect';

interface HomeHeaderProps {
  setIsLoadingStudySessionAndContent: (isLoading: boolean) => void;
  setIsLoadedStudySessionAndContent: (isLoaded: boolean) => void;
  refreshStudySessionAndContent: () => void;
}

const HomeHeader: React.FC<HomeHeaderProps> = ({
  setIsLoadingStudySessionAndContent,
  setIsLoadedStudySessionAndContent,
  refreshStudySessionAndContent,
}) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);
  const availableExams = useSelector(examsSelectors.selectAll);
  const school = useSelector((state: RootState) => state.school.school);
  const stepExams = useSelector(examsSelectors.selectStepExams);
  const shelfExams = useSelector(examsSelectors.selectShelfExams);
  const schoolExams = useSelector(examsSelectors.selectSchoolExams);
  const customExams = useSelector(examsSelectors.selectCustomExams);
  const rctExams = useSelector(examsSelectors.selectRCTExams);

  const [examMenuIsOpen, setExamMenuIsOpen] = useState(false);
  const [examSelection, setExamSelection] = useState<UserExam | null>(
    primaryExam || null
  );

  const { updateExam } = useUserExamUpdate(refreshStudySessionAndContent, {
    showSuccessMessage: false,
  });

  const examMenuButtonRef = useRef<HTMLButtonElement>(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (primaryExam && examSelection?.id !== primaryExam.id) {
      setExamSelection(primaryExam);
    }
  }, [primaryExam]);

  useEffect(() => {
    if (examSelection && examSelection.id !== primaryExam?.id) {
      setPrimaryExam();
    }
  }, [examSelection, refreshStudySessionAndContent]);

  const handleExamSelection = (exam: UserExam) => {
    setExamSelection(exam);
    setExamMenuIsOpen(false);
  };

  const menuKey = useMemo(() => availableExams.length, [availableExams]);

  const handleAddSchoolExam = () => {
    navigate('/create-exam');
    setExamMenuIsOpen(false);
    hapticsImpactLight();
  };

  const setPrimaryExam = async () => {
    if (!currentUser || !refreshStudySessionAndContent || !examSelection)
      return;

    const examData = {
      userId: currentUser.id,
      examId: examSelection.id,
      primary: true,
      ...(examSelection.selectedFlashcardCount &&
      examSelection.selectedVignetteCount
        ? {}
        : {
            selectedFlashcardCount:
              examSelection.selectedFlashcardCount ??
              defaultFlashcards(examSelection),
            selectedVignetteCount:
              examSelection.selectedVignetteCount ??
              defaultVignettes(examSelection),
          }),
    };

    await updateExam(examData);
  };

  const renderSchoolTitle = () => {
    if (schoolExams.length > 0) {
      if (school?.shortName) {
        return school?.shortName;
      } else {
        return 'School';
      }
    } else {
      return 'School';
    }
  };

  return (
    <div className="home-header">
      <div className="home-header_welcome">
        {currentUser?.profilePictureUrl && (
          <div
            className="home-header_welcome_avatar"
            role="button"
            onClick={() => {
              navigate('/account');
              hapticsImpactLight();
            }}
            tabIndex={0}
            aria-label="Navigate to account"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                navigate('/account');
                hapticsImpactLight();
              }
            }}
          >
            <img
              className="user-avatar"
              src={currentUser?.profilePictureUrl}
              alt={currentUser?.name || 'Profile Picture'}
            />
            <img
              className="blur"
              src={currentUser?.profilePictureUrl}
              alt={currentUser?.name || 'Profile Picture'}
            />
          </div>
        )}
        <div className="home-header_welcome_text">
          <div className="home-header_welcome_text_name">
            Welcome
            {!moment(currentUser?.createdAt).isSame(moment(), 'day')
              ? ' back'
              : ' '}
            {currentUser?.name?.split(' ')[0]
              ? ', ' + currentUser?.name?.split(' ')[0]
              : ''}
          </div>
          <div className="home-header_welcome_text_subtitle">
            {primaryExam?.isRCT
              ? `Get started with this baseline assessment.`
              : `Here's what Ora planned for you today, ${moment().format('MMMM D')}`}
          </div>
        </div>
      </div>
      <div className="home-header_exam-selections">
        <div className="home-header_exam-selections_top">
          <button
            className="button button--float button--exam-select"
            id="exam-menu-button"
            ref={examMenuButtonRef}
            aria-controls={examMenuIsOpen ? 'exam-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={examMenuIsOpen ? 'true' : undefined}
            onClick={() => {
              setExamMenuIsOpen(true);
              hapticsImpactLight();
            }}
            tabIndex={0}
            aria-label="Select Exam"
          >
            <div>
              <span className="label">Exam</span>
              <span className="exam-name">{examSelection?.name}</span>
            </div>
            <TriangleDown />
          </button>
        </div>
        {setIsLoadingStudySessionAndContent &&
          setIsLoadedStudySessionAndContent &&
          refreshStudySessionAndContent &&
          !examSelection?.isRCT && (
            <div className="home-header_exam-selections_bottom">
              <ExamDateSelect
                setIsLoadingStudySessionAndContent={
                  setIsLoadingStudySessionAndContent
                }
                setIsLoadedStudySessionAndContent={
                  setIsLoadedStudySessionAndContent
                }
                refreshStudySessionAndContent={refreshStudySessionAndContent}
                disabled={
                  (primaryExam?.verified &&
                    currentUser?.role !== 'admin' &&
                    !currentUser?.ambassador) ||
                  currentUser?.cohort === 'rct'
                }
              />
              {examSelection?.type !== 'CUSTOM' && !examSelection?.isRCT && (
                <TopicSelect
                  setIsLoadingStudySessionAndContent={
                    setIsLoadingStudySessionAndContent
                  }
                  setIsLoadedStudySessionAndContent={
                    setIsLoadedStudySessionAndContent
                  }
                  refreshStudySessionAndContent={refreshStudySessionAndContent}
                />
              )}
              {examSelection?.type === 'CUSTOM' && !examSelection?.isRCT && (
                <button
                  className="button button--float button--edit-schedule"
                  onClick={() => {
                    navigate('/modify-exam');
                    hapticsImpactLight();
                  }}
                  tabIndex={0}
                  aria-label="Modify Exam"
                >
                  <div className="button--edit-schedule_label">
                    {currentUser?.ambassador
                      ? 'Modify'
                      : primaryExam?.verified
                        ? 'View'
                        : 'Modify'}
                  </div>
                  <div className="button--edit-schedule_value">
                    Exam Settings
                  </div>
                </button>
              )}
              {isNbmeExam(examSelection) && (
                <WorkloadSelect
                  setIsLoadingStudySessionAndContent={
                    setIsLoadingStudySessionAndContent
                  }
                  setIsLoadedStudySessionAndContent={
                    setIsLoadedStudySessionAndContent
                  }
                  refreshStudySessionAndContent={refreshStudySessionAndContent}
                />
              )}
            </div>
          )}
        <Menu
          key={menuKey}
          id="exam-menu"
          anchorEl={examMenuButtonRef.current}
          open={examMenuIsOpen}
          onClose={() => setExamMenuIsOpen(false)}
          MenuListProps={{
            'aria-labelledby': 'exam-menu-button',
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <div className="available-exams">
            <div className="available-exams_top">
              {(currentUser?.cohort !== 'rct' ||
                (currentUser?.cohort === 'rct' &&
                  currentUser?.rctGroup === 'A')) && (
                <div className="available-exams_top_set available-exams_top_set--step">
                  <div className="available-exams_top_set_title">Step</div>
                  <div className="available-exams_top_set_exams">
                    {stepExams.map((exam) => (
                      <button
                        key={'exam-' + exam.id}
                        className={`available-exams_top_set_exams_exam ${exam.id === examSelection?.id ? 'selected' : ''}`}
                        onClick={() => handleExamSelection(exam)}
                        tabIndex={0}
                        aria-label={`Select ${exam.name}`}
                      >
                        <TriangleRight />
                        {exam.name}
                        {exam.hasCompletedRealExam && (
                          <Check className="check" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {currentUser?.cohort !== 'rct' && (
                <div className="available-exams_top_set available-exams_top_set--shelf">
                  <div className="available-exams_top_set_title">Shelf</div>
                  <div className="available-exams_top_set_exams">
                    {shelfExams.map((exam) => (
                      <button
                        key={'exam-' + exam.id}
                        className={`available-exams_top_set_exams_exam ${exam.id === examSelection?.id ? 'selected' : ''}`}
                        onClick={() => handleExamSelection(exam)}
                        tabIndex={0}
                        aria-label={`Select ${exam.name.replace('Shelf Exam', '')}`}
                      >
                        <TriangleRight />
                        {exam.name.replace('Shelf Exam', '')}
                        {exam.hasCompletedRealExam && (
                          <Check className="check" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {currentUser?.cohort !== 'rct' && schoolExams.length > 0 && (
                <div className="available-exams_top_set available-exams_top_set--school">
                  <div className="available-exams_top_set_title">
                    {renderSchoolTitle()}
                  </div>
                  <div className="available-exams_top_set_exams">
                    {schoolExams
                      .sort((a, b) => {
                        const dateA = a.examDate
                          ? new Date(a.examDate)
                          : new Date(0);
                        const dateB = b.examDate
                          ? new Date(b.examDate)
                          : new Date(0);
                        return dateA.getTime() - dateB.getTime();
                      })
                      .map((exam) => (
                        <button
                          key={'exam-' + exam.id}
                          className={`available-exams_top_set_exams_exam ${exam.id === examSelection?.id ? 'selected' : ''}`}
                          onClick={() => handleExamSelection(exam)}
                          tabIndex={0}
                          aria-label={`Select ${exam.name.replace('Shelf Exam', '')}`}
                        >
                          <TriangleRight />
                          <span className="custom-exam-name">
                            {exam.name.includes(' | ')
                              ? exam.name.split(' | ')[1]
                              : exam.name}
                          </span>
                          {exam.hasCompletedRealExam && (
                            <Check className="check" />
                          )}
                        </button>
                      ))}
                  </div>
                </div>
              )}
              {currentUser?.cohort !== 'rct' && customExams.length > 0 && (
                <div className="available-exams_top_set available-exams_top_set--custom">
                  <div className="available-exams_top_set_title">Custom</div>
                  <div className="available-exams_top_set_exams">
                    {customExams
                      .sort((a, b) => {
                        const dateA = a.examDate
                          ? new Date(a.examDate)
                          : new Date(0);
                        const dateB = b.examDate
                          ? new Date(b.examDate)
                          : new Date(0);
                        return dateA.getTime() - dateB.getTime();
                      })
                      .map((exam) => (
                        <button
                          key={'exam-' + exam.id}
                          className={`available-exams_top_set_exams_exam ${exam.id === examSelection?.id ? 'selected' : ''}`}
                          onClick={() => handleExamSelection(exam)}
                          tabIndex={0}
                          aria-label={`Select ${exam.name}`}
                        >
                          <TriangleRight />
                          <span className="custom-exam-name">
                            {exam.name.includes(' | ')
                              ? exam.name.split(' | ')[1]
                              : exam.name}
                          </span>
                          {exam.hasCompletedRealExam && (
                            <Check className="check" />
                          )}
                        </button>
                      ))}
                  </div>
                </div>
              )}
              {rctExams.length > 0 && (
                <div className="available-exams_top_set available-exams_top_set--rct">
                  <div className="available-exams_top_set_title">
                    Assessments
                  </div>
                  <div className="available-exams_top_set_exams">
                    {rctExams.map((exam) => (
                      <button
                        key={'exam-' + exam.id}
                        className={`available-exams_top_set_exams_exam ${exam.id === examSelection?.id ? 'selected' : ''}`}
                        onClick={() => handleExamSelection(exam)}
                        tabIndex={0}
                        aria-label={`Select ${exam.name}`}
                      >
                        <TriangleRight />
                        <span className="custom-exam-name">{exam.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {currentUser?.cohort !== 'rct' && (
              <div className="available-exams_bottom">
                <button
                  className="button button--link button--add-exam"
                  onClick={handleAddSchoolExam}
                  tabIndex={0}
                  aria-label="Create Custom Exam"
                >
                  <Plus />
                  Create Custom Exam
                </button>
              </div>
            )}
          </div>
        </Menu>
      </div>
    </div>
  );
};

export default HomeHeader;
